import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Send, Upload, X, Check, ChevronDown, ArrowRight, Phone, CheckCircle2, Globe2, Sparkles } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { Toaster } from "../components/ui/toaster";
import { inquiriesApi, API_BASE, imageUrl } from "../lib/api";
import axios from "axios";
import { useI18n } from "../i18n/I18nContext";
import { OPTION_CATEGORIES, translateOption, translateCategory } from "../lib/carOptions";
import { COMPANY } from "../mock/mock";

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  brand: "",
  model: "",
  first_registration: "",
  km: "",
  price_expectation: "",
  vat_car: "no",
  options: [],
  images: [],
  message: "",
};

export default function Overname() {
  const { t, lang } = useI18n();
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const uploadFiles = async (files) => {
    const list = Array.from(files).filter((f) => f.type.startsWith("image/")).slice(0, 10);
    if (!list.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      list.forEach((f) => fd.append("files", f));
      const r = await axios.post(`${API_BASE}/inquiry-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const urls = (r.data.results || []).filter((x) => x.url).map((x) => x.url);
      setForm((f) => ({ ...f, images: [...f.images, ...urls].slice(0, 10) }));
    } catch (e) {
      toast({ title: t("buy.uploadFailed"), description: e?.response?.data?.detail || String(e) });
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (i) =>
    setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  const toggleOption = (opt) =>
    setForm((f) => ({
      ...f,
      options: f.options.includes(opt) ? f.options.filter((o) => o !== opt) : [...f.options, opt],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await inquiriesApi.create({
        type: "tradein",
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        subject: `Overname - ${form.brand} ${form.model}`.trim(),
        extra: {
          brand: form.brand,
          model: form.model,
          first_registration: form.first_registration,
          km: form.km,
          price_expectation: form.price_expectation,
          vat_car: form.vat_car === "yes" ? "Ja" : "Nee",
          options: form.options.length ? form.options.join(", ") : null,
          images: form.images,
        },
      });
      toast({ title: t("sell.toastTitle"), description: t("sell.toastDesc") });
      setForm(EMPTY_FORM);
      document.getElementById("tradein-form")?.scrollIntoView({ behavior: "smooth" });
    } catch {
      toast({ title: t("car.sendFailed"), description: t("car.sendFailedDesc") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#1a1a1a] text-white min-h-screen">
      <Navbar />
      <Toaster />

      {/* Hero */}
      <section className="relative pt-[140px] pb-20 px-5 lg:px-8 overflow-hidden">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage: `url("/brand/sell-hero.jpg")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a] via-[#1a1a1a]/85 to-[#1a1a1a]" />
        <div className="relative max-w-[1100px] mx-auto text-center">
          <div className="text-[#c5a76f] text-sm tracking-[0.3em] uppercase mb-4" data-testid="tradein-overline">
            {t("tradeIn.overline")}
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
            {t("tradeIn.title")} <span className="text-[#c5a76f]">{t("tradeIn.titleAccent")}</span> {t("tradeIn.titleEnd")}
          </h1>
          <p className="text-white/75 text-lg max-w-2xl mx-auto leading-relaxed">
            {t("tradeIn.intro")}
          </p>
        </div>
      </section>

      {/* Two tracks */}
      <section className="px-5 lg:px-8 pb-20">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Track 1: stock-eligible */}
          <div className="relative p-8 lg:p-10 bg-gradient-to-br from-[#1a1a1a] to-[#242424] border border-[#c5a76f]/30 rounded-md flex flex-col" data-testid="tradein-track1">
            <div className="inline-flex items-center gap-2 self-start px-3 py-1 bg-[#c5a76f] text-black text-xs font-bold tracking-widest uppercase rounded mb-5">
              <Sparkles className="w-3.5 h-3.5" /> {t("tradeIn.track1Badge")}
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
              {t("tradeIn.track1Title")}
            </h2>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              {t("tradeIn.track1Lead")}
            </p>
            <ul className="space-y-2.5 mb-8">
              {[t("tradeIn.track1Bullet1"), t("tradeIn.track1Bullet2"), t("tradeIn.track1Bullet3")].map((b) => (
                <li key={b} className="flex items-start gap-3 text-white/85 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-[#c5a76f] flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <a
              href="#tradein-form"
              data-testid="tradein-track1-cta"
              className="mt-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#c5a76f] text-black hover:bg-[#d8bd86] transition-colors text-sm font-semibold tracking-widest uppercase rounded"
            >
              {t("tradeIn.track1Cta")} <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Track 2: non-stock */}
          <div className="relative p-8 lg:p-10 bg-[#242424] border border-white/10 rounded-md flex flex-col" data-testid="tradein-track2">
            <div className="inline-flex items-center gap-2 self-start px-3 py-1 bg-white/10 text-white text-xs font-bold tracking-widest uppercase rounded mb-5">
              <Globe2 className="w-3.5 h-3.5" /> {t("tradeIn.track2Badge")}
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
              {t("tradeIn.track2Title")}
            </h2>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              {t("tradeIn.track2Lead")}
            </p>
            <ul className="space-y-2.5 mb-8">
              {[t("tradeIn.track2Bullet1"), t("tradeIn.track2Bullet2"), t("tradeIn.track2Bullet3")].map((b) => (
                <li key={b} className="flex items-start gap-3 text-white/85 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-[#c5a76f] flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-auto flex flex-col sm:flex-row gap-3">
              <a
                href={`tel:${COMPANY.phone}`}
                data-testid="tradein-track2-call"
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white/5 hover:bg-white/10 text-white border border-white/15 hover:border-[#c5a76f]/50 transition-colors text-sm font-semibold tracking-widest uppercase rounded"
              >
                <Phone className="w-4 h-4" /> {COMPANY.phone}
              </a>
              <Link
                to="/contact"
                data-testid="tradein-track2-cta"
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#c5a76f] text-black hover:bg-[#d8bd86] transition-colors text-sm font-semibold tracking-widest uppercase rounded"
              >
                {t("tradeIn.track2Cta")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Price indication form (Track 1) */}
      <section id="tradein-form" className="px-5 lg:px-8 pb-24 scroll-mt-24">
        <div className="max-w-[900px] mx-auto bg-[#242424] border border-white/5 rounded-md p-7 lg:p-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#c5a76f] text-black text-[10px] font-bold tracking-widest uppercase rounded mb-4">
            <Sparkles className="w-3 h-3" /> {t("tradeIn.track1Badge")}
          </div>
          <h2 className="text-3xl text-white font-bold mb-2" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
            {t("tradeIn.formTitle")}
          </h2>
          <p className="text-white/65 text-sm mb-8">{t("tradeIn.formSub")}</p>
          <form onSubmit={onSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required placeholder={t("common.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tradein-name" className="bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm" />
              <input required type="email" placeholder={t("common.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="tradein-email" className="bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm" />
              <input placeholder={t("common.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="tradein-phone" className="bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm md:col-span-2" />
              <input placeholder={t("buy.brand")} value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="tradein-brand" className="bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm" />
              <input placeholder={t("buy.model")} value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="tradein-model" className="bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm" />
              <input placeholder={t("buy.firstRegistration")} value={form.first_registration} onChange={(e) => setForm({ ...form, first_registration: e.target.value })} data-testid="tradein-firstreg" className="bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm" />
              <input placeholder={t("buy.km")} value={form.km} onChange={(e) => setForm({ ...form, km: e.target.value })} data-testid="tradein-km" className="bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm" />
              <input type="number" placeholder={t("buy.priceExpectation")} value={form.price_expectation} onChange={(e) => setForm({ ...form, price_expectation: e.target.value })} data-testid="tradein-price" className="bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm" />
              <select value={form.vat_car} onChange={(e) => setForm({ ...form, vat_car: e.target.value })} data-testid="tradein-vat" className="bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm">
                <option value="no">{t("buy.vatNo")}</option>
                <option value="yes">{t("buy.vatYes")}</option>
              </select>
            </div>

            {/* Options */}
            <div className="space-y-2">
              <div className="text-white/60 text-xs uppercase tracking-wider">
                {t("buy.optionsTitle")}
                {form.options.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-[#c5a76f]/15 text-[#c5a76f] text-xs rounded font-semibold">
                    {form.options.length}
                  </span>
                )}
              </div>
              {OPTION_CATEGORIES.map((cat) => (
                <OptionGroup key={cat.label} category={cat} selected={form.options} onToggle={toggleOption} lang={lang} />
              ))}
            </div>

            {/* Photos */}
            <div className="space-y-2">
              <div className="text-white/60 text-xs uppercase tracking-wider">
                {t("buy.photosTitle")}{" "}
                <span className="text-white/40 normal-case">({form.images.length}/10)</span>
              </div>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading || form.images.length >= 10}
                data-testid="tradein-upload-button"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-white/15 hover:border-[#c5a76f]/50 text-white/80 hover:text-[#c5a76f] text-sm rounded-md transition-colors disabled:opacity-50"
              >
                <Upload className="w-4 h-4" />
                {uploading ? t("buy.uploading") : t("buy.addPhotos")}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  if (e.target.files?.length) uploadFiles(e.target.files);
                  e.target.value = "";
                }}
                className="hidden"
              />
              {form.images.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 pt-2">
                  {form.images.map((u, i) => (
                    <div key={i} className="relative aspect-square rounded overflow-hidden bg-[#1a1a1a] border border-white/5 group">
                      <img src={imageUrl(u)} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/80 text-white hover:bg-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Remove"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <textarea placeholder={t("common.message")} rows="4" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="tradein-message" className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#c5a76f] outline-none rounded px-4 py-3 text-white text-sm" />
            <button disabled={loading || uploading} data-testid="tradein-submit" className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#c5a76f] text-black hover:bg-[#d8bd86] transition-colors text-sm font-semibold tracking-widest uppercase disabled:opacity-60 rounded">
              {loading ? t("common.sending") : t("buy.send")} <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function OptionGroup({ category, selected, onToggle, lang }) {
  const [open, setOpen] = useState(false);
  const count = category.items.filter((i) => selected.includes(i)).length;
  return (
    <div className="border border-white/5 rounded bg-[#1a1a1a] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-white text-sm">{translateCategory(category.label, lang)}</span>
          {count > 0 && (
            <span className="px-1.5 py-0.5 bg-[#c5a76f]/15 text-[#c5a76f] text-[10px] rounded font-semibold">
              {count}
            </span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-3 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {category.items.map((opt) => {
            const checked = selected.includes(opt);
            return (
              <label
                key={opt}
                className={`flex items-center gap-2 px-2.5 py-2 rounded text-xs cursor-pointer transition-colors ${
                  checked
                    ? "bg-[#c5a76f]/15 text-[#c5a76f] border border-[#c5a76f]/30"
                    : "bg-white/[0.02] text-white/75 hover:bg-white/[0.05] border border-transparent"
                }`}
              >
                <input type="checkbox" checked={checked} onChange={() => onToggle(opt)} className="sr-only" />
                <span
                  className={`w-3.5 h-3.5 rounded flex items-center justify-center flex-shrink-0 border ${
                    checked ? "bg-[#c5a76f] border-[#c5a76f]" : "border-white/30"
                  }`}
                >
                  {checked && <Check className="w-2.5 h-2.5 text-black" strokeWidth={3} />}
                </span>
                <span className="truncate">{translateOption(opt, lang)}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
